import axios from 'axios';
import * as common from './common';

const baseUrl = common.getApiUrl();

/**
 * 토큰 만료 확인
 * @param {string} token - JWT 토큰
 * @returns {boolean} - 만료 여부
 */
export function isTokenExpired(token) {
    if (!token) return true;

    try {
        const [, payload] = token.split('.');
        if (!payload) return true;

        const decoded = JSON.parse(atob(payload));
        const currentTime = Date.now() / 1000;

        // 5분 여유를 두고 만료 확인 (토큰 갱신을 미리 준비)
        return decoded.exp < (currentTime + 300);
    } catch (error) {
        console.error("토큰 디코딩 오류:", error);
        return true;
    }
}

/**
 * 액세스 토큰 갱신
 * @param {string} refreshToken - 리프레시 토큰
 * @returns {Promise<string|null>} - 새로운 액세스 토큰 또는 null
 */
export async function refreshAccessToken(refreshToken) {
    if (!refreshToken) {
        console.warn('리프레시 토큰이 없습니다');
        return null;
    }

    try {
        console.log('토큰 갱신 요청 중...');

        const response = await axios.post(`${baseUrl}/auth/refresh-token`, {
            refreshToken
        });

        if (response.data) {
            console.log('토큰 갱신 성공');
            localStorage.setItem('access_token', response.data);
            return response.data;
        } else {
            console.warn('토큰 갱신 응답이 비어있음');
            return null;
        }
    } catch (error) {
        console.error('토큰 갱신 실패:', error);

        // 401 또는 403 에러인 경우 리프레시 토큰도 만료된 것으로 간주
        if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('리프레시 토큰 만료');
            clearAllTokens();
        }

        return null;
    }
}

/**
 * 토큰 유효성 검사 및 자동 갱신
 * @returns {Promise<boolean>} - 인증 성공 여부
 */
export async function validateAndRefreshToken() {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    // 리프레시 토큰이 없으면 인증 실패
    if (!refreshToken) {
        console.log('리프레시 토큰 없음');
        return false;
    }

    // 액세스 토큰이 유효한 경우
    if (accessToken && !isTokenExpired(accessToken)) {
        console.log('액세스 토큰 유효');
        return true;
    }

    // 액세스 토큰이 만료된 경우 갱신 시도
    console.log('액세스 토큰 만료 - 갱신 시도');
    const newAccessToken = await refreshAccessToken(refreshToken);

    return newAccessToken !== null;
}

/**
 * 모든 토큰 제거
 */
export function clearAllTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('role');
    localStorage.removeItem('memberId');
    localStorage.removeItem('memberNm');
    console.log('모든 토큰 제거됨');
}

/**
 * 토큰 만료 시간 확인 (디버깅용)
 * @param {string} token - JWT 토큰
 * @returns {Date|null} - 만료 시간
 */
export function getTokenExpiration(token) {
    if (!token) return null;

    try {
        const [, payload] = token.split('.');
        const decoded = JSON.parse(atob(payload));
        return new Date(decoded.exp * 1000);
    } catch (error) {
        console.error("토큰 만료시간 확인 오류:", error);
        return null;
    }
}

/**
 * Axios 인터셉터 설정 - 자동 토큰 갱신
 */
export function setupTokenInterceptor() {
    // 요청 인터셉터 - 토큰 자동 첨부 및 갱신
    axios.interceptors.request.use(
        async (config) => {
            // 로그인, 토큰 갱신 요청은 제외
            if (config.url?.includes('/auth/login') || config.url?.includes('/auth/refresh-token')) {
                return config;
            }

            const token = localStorage.getItem('access_token');

            if (token) {
                // 토큰이 만료되었으면 갱신 시도
                if (isTokenExpired(token)) {
                    const refreshed = await validateAndRefreshToken();

                    if (!refreshed) {
                        // 갱신 실패 시 로그인 페이지로 리다이렉트
                        window.location.href = '/login';
                        return Promise.reject(new Error('토큰 갱신 실패'));
                    }

                    // 갱신된 토큰으로 헤더 설정
                    const newToken = localStorage.getItem('access_token');
                    config.headers.Authorization = `Bearer ${newToken}`;
                } else {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // 응답 인터셉터 - 401 에러 처리
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // 401 에러이고 재시도하지 않은 요청인 경우
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                const refreshed = await validateAndRefreshToken();

                if (refreshed) {
                    // 갱신 성공 시 원래 요청 재시도
                    const newToken = localStorage.getItem('access_token');
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } else {
                    // 갱신 실패 시 로그인 페이지로 리다이렉트
                    clearAllTokens();
                    window.location.href = '/login';
                }
            }

            return Promise.reject(error);
        }
    );
}